import { Icon } from "@mdi/react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { DataEditorRef } from "@glideapps/glide-data-grid";
import { GridOn } from "@material-ui/icons";
import { mdiDownload } from "@mdi/js";
import { Button, Input, Modal, Tooltip } from "antd";
import { useAtomValue, useSetAtom } from "jotai";
import { requestAddNewField as addField } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import * as XLSX from "xlsx";

import { useGroupByState } from "components/groupBy/group-by.context";
import { useSelectedCategory } from "components/groupBy/hooks";

import FieldGroupGlideGrid from "./FieldGroupGlideGrid";
import {
  columnUploadedSignalAtom,
  columnsAtom,
  filteredFormattedUwisAtom,
  filteredUwisAtom,
  hasChangesAtom,
  modalStateAtom
} from "./atoms/fieldGroupAtoms";
import { useFieldGroupEditor } from "./hooks";

interface FieldGroupEditorProps {
  visible: boolean;
  fieldGroup: string;
  showDiscardModal: boolean;
  onShowDiscardModal: (isVisible: boolean) => void;
  onClose: () => void;
  onSave?: () => void;
}

export default function FieldGroupEditor({
  visible,
  fieldGroup,
  showDiscardModal,
  onShowDiscardModal,
  onClose,
  onSave
}: FieldGroupEditorProps) {
  const dispatch = useDispatch();
  const { entityKind } = useGroupByState();
  const { isOrgDefinedColumn, isProjectDefinedColumn } = useSelectedCategory();
  const selectedProjectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );

  const gridRef = useRef<DataEditorRef>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const hasChanges = useAtomValue(hasChangesAtom);
  const modalState = useAtomValue(modalStateAtom);
  const setModalState = useSetAtom(modalStateAtom);
  const columns = useAtomValue(columnsAtom);
  const uwis = useAtomValue(filteredUwisAtom);
  const formattedUwis = useAtomValue(filteredFormattedUwisAtom);
  const columnUploadedSignal = useAtomValue(columnUploadedSignalAtom);

  const {
    isSaving,
    isLoading,
    validationError,
    duplicateColumnName,
    handleSave: saveChanges,
    resetAllState,
    refetchFieldGroupData
  } = useFieldGroupEditor({
    isVisible: visible,
    fieldGroup,
    isClosing
  });

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);

      // Build the data array with headers and rows
      const headers = ["UWI", ...columns.map((col) => col.name)];
      const data = uwis.map((uwi, idx) => [
        formattedUwis[idx] ?? uwi,
        ...columns.map((col) => col.data[uwi] || "")
      ]);

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

      // Auto-size columns
      const maxWidth = 20;
      const columnWidths = headers.map((header, idx) => {
        const headerWidth = header.length;
        const maxDataWidth = data.reduce(
          (max, row) => Math.max(max, String(row[idx]).length),
          0
        );
        return Math.min(Math.max(headerWidth, maxDataWidth) + 2, maxWidth);
      });
      worksheet["!cols"] = columnWidths.map((width) => ({ wch: width }));

      // Create workbook and add sheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Field Group");

      // Download file
      XLSX.writeFile(workbook, `${fieldGroup}.xlsx`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Reset isClosing when modal opens
  useEffect(() => {
    if (visible) {
      setIsClosing(false);
      refetchFieldGroupData();
    }
  }, [visible, refetchFieldGroupData]);

  // Refetch when column upload succeeds
  useEffect(() => {
    if (columnUploadedSignal > 0 && visible) {
      refetchFieldGroupData();
    }
  }, [columnUploadedSignal, visible, refetchFieldGroupData]);
  // Reset edits when modal closes
  useEffect(() => {
    if (!visible) {
      resetAllState();
    }
  }, [visible, resetAllState]);

  // Reset all state when switching to different field group
  useEffect(() => {
    resetAllState();
  }, [fieldGroup, resetAllState]);

  const handleSave = async () => {
    setIsClosing(true);
    const success = await saveChanges();
    if (success) {
      onSave?.();
      onClose();
    } else {
      // Save failed, reset isClosing so loading state works again
      setIsClosing(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      onShowDiscardModal(true);
    } else {
      setIsClosing(true);
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    onShowDiscardModal(false);
    setIsClosing(true);
    onClose();
  };

  const handleDiscardCancel = () => {
    onShowDiscardModal(false);
  };

  return (
    <>
      <StyledModal
        title={`Edit Field Group: ${fieldGroup}`}
        open={visible}
        onCancel={handleCancel}
        destroyOnClose
        width="90vw"
        style={{ top: 20 }}
        zIndex={1100}
        keyboard={true}
        footer={
          <FooterContainer>
            {validationError && (
              <ErrorMessage>
                <ExclamationCircleOutlined /> {validationError}
              </ErrorMessage>
            )}
            <LeftButtonGroup>
              <DownloadTemplateButton
                type="primary"
                icon={<Icon path={mdiDownload} size={1} />}
                loading={isDownloading}
                onClick={handleDownloadExcel}>
                Download Excel (.xlsx)
              </DownloadTemplateButton>
              <Tooltip title={"Add Excel Fields"}>
                <Button
                  icon={<GridOn fontSize="small" style={{ marginRight: "6px" }} />}
                  onClick={() => {
                    dispatch(
                      addField({
                        type: "Excel",
                        isOrg: isOrgDefinedColumn,
                        projectId: isProjectDefinedColumn ? selectedProjectId : undefined,
                        entityKind,
                        fieldGroup
                      })
                    );
                  }}>
                  Add Excel Fields
                </Button>
              </Tooltip>
            </LeftButtonGroup>
            <RightButtonGroup>
              <Button onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
              <Tooltip
                overlayStyle={{ zIndex: 3001 }}
                title={!hasChanges ? "Commit changes to enable save" : ""}>
                <Button
                  type="primary"
                  onClick={handleSave}
                  loading={isSaving}
                  disabled={!hasChanges}>
                  Save
                </Button>
              </Tooltip>
            </RightButtonGroup>
          </FooterContainer>
        }>
        <EditorContainer>
          <FieldGroupGlideGrid
            key={fieldGroup}
            loading={isLoading}
            duplicateColumnName={duplicateColumnName}
            gridRef={gridRef}
            onRequestClose={handleCancel}
          />
        </EditorContainer>
      </StyledModal>
      {/* Add Column Modal */}
      <Modal
        title="Add New Column"
        open={modalState.showAddColumn}
        onOk={() => {
          const trimmedName = modalState.newColumnName.trim();
          if (!trimmedName) {
            setModalState((prev) => ({
              ...prev,
              addColumnError: "Column name cannot be empty"
            }));
            return;
          }
          setModalState((prev) => ({
            ...prev,
            showAddColumn: false,
            newColumnName: "",
            addColumnError: null
          }));
        }}
        onCancel={() =>
          setModalState((prev) => ({
            ...prev,
            showAddColumn: false,
            newColumnName: "",
            addColumnError: null
          }))
        }
        okText="Add"
        cancelText="Cancel"
        zIndex={1100}>
        <ModalContent>
          <label>Column Name:</label>
          <Input
            placeholder="Enter column name"
            value={modalState.newColumnName}
            onChange={(e) =>
              setModalState((prev) => ({
                ...prev,
                newColumnName: e.target.value,
                addColumnError: null
              }))
            }
            onPressEnter={() => {
              const trimmedName = modalState.newColumnName.trim();
              if (!trimmedName) {
                setModalState((prev) => ({
                  ...prev,
                  addColumnError: "Column name cannot be empty"
                }));
                return;
              }
              setModalState((prev) => ({
                ...prev,
                showAddColumn: false,
                newColumnName: "",
                addColumnError: null
              }));
            }}
            autoFocus
            status={modalState.addColumnError ? "error" : undefined}
          />
          {modalState.addColumnError && (
            <ErrorMessage>{modalState.addColumnError}</ErrorMessage>
          )}
        </ModalContent>
      </Modal>

      {/* Discard Changes Modal */}
      <Modal
        title="Discard changes?"
        open={showDiscardModal}
        onOk={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
        okText="Discard"
        cancelText="Keep Editing"
        okButtonProps={{ danger: true }}
        zIndex={1102}>
        <p>You have unsaved changes. Are you sure you want to close?</p>
      </Modal>
    </>
  );
}

const EditorContainer = styled.div`
  width: 100%;
  height: calc(80vh - 120px);
  min-height: 500px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const DownloadTemplateButton = styled(Button)`
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
`;

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
  flex-wrap: wrap;
  row-gap: 12px;
`;

const LeftButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;

  .ant-btn + .ant-btn {
    margin-left: 0;
  }
`;

const RightButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;

  .ant-btn + .ant-btn {
    margin-left: 0;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-weight: 500;
  flex: 1;
  text-align: left;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
  margin-right: 16px;

  .anticon {
    font-size: 16px;
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
    display: flex;
    flex-direction: column;
    min-height: 500px;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;

  label {
    font-weight: 500;
  }
`;
